/* Product page on a phone: three things that made it unusable, each undone without touching the
   theme's own scripts.
   1. The bulk-order list (one stepper row per variant, often 14+) was 4,000 px of scrolling between
      the description and the form. It is now a folded panel with a count; the buy box above already
      covers the single-variant order.
   2. The Overview prose (a generic essay on some kits) pushed the parts and data down a screen and a
      half. It is clamped with "Read more" — on every width since 2026-10-08 (on a desktop it was a
      screen of left-column prose beside an empty right side).
   3. The tab bar scrolls sideways with nothing to say so. The active tab is centred when chosen, the bar
      fades where more tabs wait, and arrow buttons scroll it (2026-10-08). */
(() => {
  const phone = window.matchMedia('(max-width: 749px)');

  /* 1 · bulk-order list */
  const foldOrderList = () => {
    const section = document.querySelector('[id$="__quick-order-list"]');
    if (!section || section.dataset.dnFolded) return;
    const rows = section.querySelectorAll('tbody tr, .quick-order-list__row').length;
    const variants = new Set([...section.querySelectorAll('.variant-item__name')].map((e) => e.textContent.trim())).size;
    const count = variants || Math.round(rows / 3) || rows;
    section.dataset.dnFolded = '1';
    const button = document.createElement('button');
    button.type = 'button'; button.className = 'dn-fold__toggle'; button.setAttribute('aria-expanded', 'false');
    button.innerHTML = '<span>Order several sizes at once' + (count ? ' <small>' + count + ' variants</small>' : '') + '</span><svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m4 6 4 4 4-4"/></svg>';
    section.insertBefore(button, section.firstChild);
    section.classList.add('dn-fold');
    const apply = () => { const open = !phone.matches; section.classList.toggle('is-open', open); button.setAttribute('aria-expanded', String(open)); };
    button.addEventListener('click', () => { const open = !section.classList.contains('is-open'); section.classList.toggle('is-open', open); button.setAttribute('aria-expanded', String(open)); });
    phone.addEventListener('change', apply); apply();
  };

  /* 2 · overview prose */
  const clampOverview = () => {
    document.querySelectorAll('.dnd__overview').forEach((box) => {
      if (box.dataset.dnClamp) return;
      if (!box.offsetHeight) return;
      box.dataset.dnClamp = '1';
      if (box.scrollHeight < 340) return;
      box.classList.add('is-clamped');
      const more = document.createElement('button');
      more.type = 'button'; more.className = 'dn-more'; more.textContent = 'Read more';
      more.addEventListener('click', () => { const open = box.classList.toggle('is-clamped') === false; more.textContent = open ? 'Show less' : 'Read more'; });
      box.after(more);
    });
  };

  /* 3 · tab bar */
  const tabBar = () => {
    document.querySelectorAll('.dnd__bar').forEach((bar) => {
      if (bar.dataset.dnHint) return;
      bar.dataset.dnHint = '1';
      // arrows either side of the bar, shown only while there are tabs out of view that way: the fade
      // alone said "more" without offering a way to get there that does not need a trackpad or a swipe
      const arrow = (dir) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = `dnd__arrow dnd__arrow--${dir}`; b.hidden = true;
        b.setAttribute('aria-label', dir === 'prev' ? 'Scroll tabs left' : 'Scroll tabs right');
        b.innerHTML = `<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${dir === 'prev' ? 'M10 3 5 8l5 5' : 'm6 3 5 5-5 5'}"/></svg>`;
        b.addEventListener('click', () => bar.scrollBy({ left: (dir === 'prev' ? -1 : 1) * bar.clientWidth * 0.7, behavior: 'smooth' }));
        return b;
      };
      const prev = arrow('prev'), next = arrow('next');
      bar.before(prev); bar.after(next);
      bar.parentElement.classList.add('has-arrows');
      const update = () => {
        const max = bar.scrollWidth - bar.clientWidth;
        bar.classList.toggle('can-right', max > 4 && bar.scrollLeft < max - 4);
        bar.classList.toggle('can-left', bar.scrollLeft > 4);
        prev.hidden = !bar.classList.contains('can-left');
        next.hidden = !bar.classList.contains('can-right');
      };
      bar.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      bar.addEventListener('click', (e) => {
        const tab = e.target.closest('.dnd__tab');
        if (tab) setTimeout(() => { const left = tab.offsetLeft - (bar.clientWidth - tab.offsetWidth) / 2; bar.scrollTo({ left, behavior: 'smooth' }); }, 30);
      });
      update();
      setTimeout(update, 400);
    });
  };

  const run = () => { foldOrderList(); clampOverview(); tabBar(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
  window.addEventListener('load', () => { run(); setTimeout(run, 600); });
  phone.addEventListener('change', clampOverview); // a hidden panel (offsetHeight 0) is clamped once it shows
})();

/* The selected variant, carried to the two places that do not re-render with it:
   1. the dimension tables in the Datasheet / component tabs mark the chosen DN's row, so a buyer
      reads their size without hunting for it;
   2. the "Request a quote" link carries the configuration (title · SKU · variant) to the quote form.
   Trade publishes variant changes on its pub/sub bus (PUB_SUB_EVENTS.variantChange). */
(() => {
  const dnOf = (values) => {
    for (const v of values) { const m = String(v || '').match(/\bDN\s?(\d{1,4})\b/i); if (m) return m[1]; }
    return null;
  };
  const markRows = (dn) => {
    document.querySelectorAll('.dnd-table tr.is-current').forEach((tr) => tr.classList.remove('is-current'));
    if (!dn) return;
    document.querySelectorAll('.dnd-table table').forEach((table) => {
      [...table.rows].forEach((tr) => {
        const first = tr.cells[0] && tr.cells[0].textContent.replace(/\s+/g, '').toUpperCase();
        if (first === dn || first === 'DN' + dn) tr.classList.add('is-current');
      });
    });
  };
  const quote = (variant) => {
    const link = document.querySelector('[data-dn-quote]');
    if (!link || !variant) return;
    const parts = [link.dataset.productTitle];
    if (variant.sku) parts.push('SKU ' + variant.sku);
    if (variant.title && variant.title !== 'Default Title') parts.push(variant.title);
    const url = new URL(link.href, location.origin);
    url.searchParams.set('config', parts.join(' | '));
    link.href = url.pathname + url.search;
  };
  const selectedValues = () => [...document.querySelectorAll('variant-selects input:checked, variant-selects select')].map((el) => el.value);
  const init = () => markRows(dnOf(selectedValues()));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event && event.data && event.data.variant;
      markRows(dnOf(variant ? variant.options : selectedValues()));
      quote(variant);
    });
  }
})();

/* Quick order ("Order several sizes"): Trade's quick-order list, made easier to scan and fill.
   - sizes in numeric order (half the catalogue stores DN100 before DN50); only when EVERY row names a DN
   - a visible heading (the section's own is screen-reader only)
   - each name "DN50 / 16 bar" split into the size and its pressure, so the size column reads at a glance
   - a row with a quantity is marked (is-ordered), the size chosen in the buy box too (is-current)
   Trade re-renders the table after every quantity change, so all of it is idempotent and re-applied by
   a MutationObserver — a one-shot pass was undone by the first click on "+". */
(() => {
  // read the size from the split-off span once it exists: the joined text "DN1064 bar" would read as DN1064
  const dn = (tr) => { const m = (tr.querySelector('.dn-qo-size') || tr.querySelector('[data-dn-row-name]') || tr).textContent.match(/\bDN\s?(\d{1,4})\b/i); return m ? Number(m[1]) : NaN; };
  let currentDn = null;
  const decorate = (section) => {
    const body = section.querySelector('tbody');
    if (body) {
      const rows = [...body.querySelectorAll('tr.variant-item')];
      const sorted = [...rows].sort((x, y) => dn(x) - dn(y));
      if (rows.length > 1 && rows.every((r) => !Number.isNaN(dn(r))) && sorted.some((r, i) => r !== rows[i])) sorted.forEach((r) => body.append(r));
      rows.forEach((tr) => {
        tr.querySelectorAll('[data-dn-row-name]:not([data-dn-split])').forEach((name) => {
          const [size, ...rest] = name.textContent.split(' / ');
          name.dataset.dnSplit = '1';
          if (!rest.length) return;
          name.textContent = '';
          const strong = document.createElement('span'); strong.className = 'dn-qo-size'; strong.textContent = size.trim();
          const sub = document.createElement('span'); sub.className = 'dn-qo-sub'; sub.textContent = rest.join(' / ').trim();
          name.append(strong, sub);
        });
        const input = tr.querySelector('.quantity__input');
        tr.classList.toggle('is-ordered', !!input && Number(input.value) > 0);
        tr.classList.toggle('is-current', currentDn != null && dn(tr) === currentDn);
      });
    }
    const table = section.querySelector('.quick-order-list__table, table');
    if (table && !section.querySelector('.dn-qo-title')) {
      const h = document.createElement('h2'); h.className = 'dn-qo-title'; h.textContent = 'Order several sizes';
      (table.closest('.quick-order-list__container, form, .page-width') || table.parentNode).prepend(h);
    }
  };
  const run = () => {
    const section = document.querySelector('[id$="__quick-order-list"]');
    if (!section) return;
    decorate(section);
    if (section.dataset.dnObserved) return;
    section.dataset.dnObserved = '1';
    let queued = 0;
    new MutationObserver(() => { cancelAnimationFrame(queued); queued = requestAnimationFrame(() => decorate(section)); })
      .observe(section, { childList: true, subtree: true });
    section.addEventListener('input', (e) => { const tr = e.target.closest && e.target.closest('tr.variant-item'); if (tr) tr.classList.toggle('is-ordered', Number(e.target.value) > 0); });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const v = event && event.data && event.data.variant;
        const m = v && (v.options || []).join(' ').match(/\bDN\s?(\d{1,4})\b/i);
        currentDn = m ? Number(m[1]) : null; decorate(section);
      });
    }
    const picked = [...document.querySelectorAll('variant-selects input:checked, variant-selects select')].map((el) => el.value).join(' ').match(/\bDN\s?(\d{1,4})\b/i);
    if (picked) { currentDn = Number(picked[1]); decorate(section); }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
})();

/* Copyable codes in the title block (model, SKU, the full store title). The SKU span is the one Trade
   rewrites on a variant change, so the value is read at click time, never cached. */
(() => {
  let status = null;
  const announce = (text) => {
    if (!status) {
      status = document.createElement('span');
      status.className = 'visually-hidden'; status.setAttribute('role', 'status');
      document.body.append(status);
    }
    status.textContent = ''; requestAnimationFrame(() => { status.textContent = text; });
  };
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-dn-copy]');
    if (!button) return;
    const holder = button.closest('.dn-code, .dn-id__full-body');
    const source = holder && holder.querySelector('[data-dn-copy-value]');
    const text = source ? source.textContent.trim() : '';
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const range = document.createRange(); range.selectNodeContents(source);
      const selection = getSelection(); selection.removeAllRanges(); selection.addRange(range);
      document.execCommand('copy'); selection.removeAllRanges();
    }
    button.dataset.copied = '';
    announce(`${button.dataset.done || 'Copied'}: ${text}`);
    clearTimeout(button.dnTimer);
    button.dnTimer = setTimeout(() => delete button.dataset.copied, 1600);
  });
})();

/* Spec pills light their part on the gallery photo. The outlines are the same reviewed SVG paths the
   "In detail" spotlight uses (dnova.highlights, 0–1000 space over the photo), drawn over whichever gallery
   slide shows that photo — matched by file name, never assumed to be the first. A click (tap, Enter) chooses a
   part and brings the photo into view; a second click or Esc lets go. A mouse hover previews too, but
   nothing depends on it — touch chooses by tapping. The overlay
   sits inside the slide with pointer-events off, so zoom and swipe still work. */
(() => {
  const NS = 'http://www.w3.org/2000/svg';
  const baseName = (url) => decodeURIComponent(String(url || '').split('?')[0].split('/').pop())
    .replace(/\.(jpe?g|png|webp|gif)$/i, '').replace(/_\d+x\d*$/, '').toLowerCase();

  const init = () => {
    const spec = document.querySelector('[data-dn-spec]');
    if (!spec || spec.dataset.dnReady) return;
    spec.dataset.dnReady = '1';
    const data = JSON.parse(spec.querySelector('[data-dn-spec-outlines]').textContent);
    const parts = data.parts || {};
    const photo = baseName(data.photo);
    const scope = spec.closest('.shopify-section') || document;
    const slide = [...scope.querySelectorAll('.product__media-item')].find((li) => {
      const img = li.querySelector('img');
      return img && baseName(img.currentSrc || img.src) === photo;
    });
    const pills = [...spec.querySelectorAll('[data-dn-light]')];
    // no slide shows the outlined photo: the pills could light nothing, so they do not show
    if (!slide) { spec.hidden = true; return; }
    const container = slide.querySelector('.product-media-container') || slide;
    const img = slide.querySelector('img');

    // The chooser sits in its own panel at the foot of the gallery's sticky column (after the thumbnails),
    // so it travels with the photo it lights instead of scrolling away under it.
    const column = slide.closest('media-gallery, .product__column-sticky');
    if (column) column.append(spec);
    spec.classList.add('dn-parts--panel');

    const layer = document.createElement('div');
    layer.className = 'dn-light'; layer.setAttribute('aria-hidden', 'true');
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', '0 0 1000 1000'); svg.setAttribute('preserveAspectRatio', 'none');
    const id = `dn-light-${Math.random().toString(36).slice(2, 8)}`;
    svg.innerHTML = `<defs><filter id="${id}-f" x="-10%" y="-10%" width="120%" height="120%"><feGaussianBlur stdDeviation="4"/></filter>`
      + `<mask id="${id}-m" maskUnits="userSpaceOnUse" x="0" y="0" width="1000" height="1000"><rect width="1000" height="1000" fill="#fff"/>`
      + `<path fill="#000" fill-rule="evenodd" filter="url(#${id}-f)"/></mask></defs>`
      + `<rect class="dn-light__dim" width="1000" height="1000" mask="url(#${id}-m)"/><path class="dn-light__outline" fill-rule="evenodd"/>`;
    const hole = svg.querySelector('mask path'), outline = svg.querySelector('.dn-light__outline');
    layer.append(svg);
    container.append(layer);

    // the photo's own box inside the slide (object-fit: contain leaves mist bands around it)
    const place = () => {
      const box = img.getBoundingClientRect(), frame = container.getBoundingClientRect();
      const ratio = (data.width && data.height) ? data.width / data.height : (img.naturalWidth / img.naturalHeight || 1);
      let w = box.width, h = box.height;
      if (w / h > ratio) w = h * ratio; else h = w / ratio;
      Object.assign(layer.style, {
        left: `${box.left - frame.left + (box.width - w) / 2}px`, top: `${box.top - frame.top + (box.height - h) / 2}px`,
        width: `${w}px`, height: `${h}px`,
      });
    };

    let pinned = null;
    const draw = (pill) => {
      const list = pill ? pill.dataset.dnLight.split(',').map((k) => parts[k]).filter(Boolean) : [];
      if (!list.length) { layer.removeAttribute('data-on'); return; }
      const d = list.map((p) => (pill.hasAttribute('data-outer') && p.outer ? p.outer : p.d)).join('');
      place();
      hole.setAttribute('d', d); outline.setAttribute('d', d);
      layer.dataset.on = '';
    };
    const showPhoto = () => {
      if (!slide.classList.contains('is-active')) {
        const thumb = scope.querySelector(`[data-target="${slide.dataset.mediaId}"] button`);
        if (thumb && thumb.offsetParent) thumb.click();
        else slide.parentElement.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }
      const r = container.getBoundingClientRect();
      if (r.bottom < 80 || r.top > innerHeight - 80) container.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    const pin = (pill) => {
      pinned = pinned === pill ? null : pill;
      pills.forEach((p) => p.setAttribute('aria-pressed', String(p === pinned)));
      if (pinned) { showPhoto(); pinned.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' }); }
      draw(pinned);
    };

    pills.forEach((pill) => {
      if (!pill.dataset.dnLight.split(',').some((k) => parts[k])) { pill.classList.remove('dn-pill--lit'); return; }
      pill.addEventListener('click', () => pin(pill));
      // hover previews on a pointer that can hover; it never replaces the click, which is what touch uses
      pill.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse' && slide.classList.contains('is-active')) draw(pill); });
      pill.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') draw(pinned); });
    });
    spec.addEventListener('keydown', (event) => { if (event.key === 'Escape' && pinned) pin(pinned); });
    addEventListener('resize', () => { if (layer.hasAttribute('data-on')) place(); }, { passive: true });
    if (img && !img.complete) img.addEventListener('load', () => { if (layer.hasAttribute('data-on')) place(); }, { once: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
